import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint, debounce } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /**
   * How much room the item list must keep, in pixels, for the summary to stay
   * pinned.
   *
   * The reasoning behind unpinning at all: a sticky summary holds its space
   * permanently, so past a certain size it leaves only a sliver for the items
   * to scroll in. Letting it scroll with everything else gives the list the
   * full height back while the customer is reading it.
   *
   * This used to be a share of the drawer — 0.5, then 0.7 — and that is what
   * kept going wrong. This summary carries more than a plain Horizon one:
   * savings banner, trust columns, payment badges, and now the add-on
   * switches. Every row added to it moved the trip point, so the drawer
   * started unpinning on ordinary laptops for a reason that had nothing to do
   * with whether the list was still usable. Measuring the room left asks the
   * question that actually matters, and does not need retuning the next time
   * the summary grows a row.
   *
   * Measured against the scrolling container, not the dialog: the header and
   * the boosters bar sit outside it and take about 117px, so counting them as
   * room for the list overstates it by that much.
   *
   * 60px is half a product row - deliberately low. On every real screen the
   * summary stays pinned, which is the behaviour being asked for; the guard
   * is left in place only for the degenerate case where the summary is taller
   * than the drawer itself and pinning it would hide the list completely.
   *
   * @type {number}
   */
  #minimumItemsRoom = 60;

  /**
   * Whether the phone's back button should close the drawer.
   *
   * Off, and the history entry that made it work is no longer pushed.
   *
   * What it bought: on mobile, opening the drawer added a history entry, so
   * the back button closed the drawer instead of leaving the page.
   *
   * What it cost: that entry is created after the body has been fixed in
   * place for the scroll lock, so the scroll position stored with it is zero.
   * Closing the drawer calls history.back(), the browser restores the position
   * it recorded — the top of the page — and the customer loses their place.
   * Restoring the position by script does not hold, because the browser's own
   * restore lands afterwards and wins.
   *
   * Since the drawer can still be closed by its own button, by tapping outside
   * it, or with Escape, the back button was the lesser of the two. On mobile
   * it now leaves the page as it would anywhere else.
   *
   * Set to true to put the old behaviour back.
   *
   * @type {boolean}
   */
  #closeOnBackButton = false;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#restartOpenAnimation);
    this.addEventListener(DialogOpenEvent.eventName, this.#scheduleStickyUpdate);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    window.addEventListener('resize', this.#handleStickyResize);

    // Clears an entry left behind by an earlier visit, from before this was
    // switched off: without it, the first close of the session would still
    // call history.back().
    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#restartOpenAnimation);
    this.removeEventListener(DialogOpenEvent.eventName, this.#scheduleStickyUpdate);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    window.removeEventListener('resize', this.#handleStickyResize);
    this.#historyAbortController?.abort();
  }

  /**
   * Restarts the drawer's opening keyframes.
   *
   * Both directions are plain CSS animations declared on `.dialog-drawer`
   * (`move-and-fade` in base.css); only the closing one is explicitly
   * restarted, by `closeDialog` in dialog.js. WebKit does not reliably replay
   * the opening keyframes when the dialog is promoted to the top layer, so the
   * drawer appears with no animation at all while the closing one still runs.
   * This mirrors what `closeDialog` already does, scoped to the cart drawer so
   * the shared dialog component is left untouched.
   *
   * Runs on DialogOpenEvent, which is dispatched in the same frame as
   * `showModal()`, so the restart happens before the first painted frame.
   */
  #restartOpenAnimation = () => {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    dialog.style.animation = 'none';
    // Forced reflow: drops the cancelled animation before the stylesheet one is restored.
    void dialog.offsetWidth;
    dialog.style.animation = '';
  };

  #handleHistoryOpen = () => {
    if (!this.#closeOnBackButton) return;
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    if (!this.#closeOnBackButton) return;

    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);

    // `CartAddEvent.eventName` is `cart:update`, so this also covers quantity
    // changes and removals: the summary's height moves with them.
    this.#scheduleStickyUpdate();
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  /**
   * Defers the measurement by two frames.
   *
   * DialogOpenEvent fires in the same frame as `showModal()`, before WebKit has
   * laid out the dialog it has just promoted to the top layer. Measuring there
   * returned a height of 0, the ratio below came out as Infinity, and the
   * summary was pinned unsticky for the whole session.
   */
  #scheduleStickyUpdate = () => {
    requestAnimationFrame(() => requestAnimationFrame(this.#updateStickyState));
  };

  #handleStickyResize = debounce(() => {
    if (this.refs.dialog?.open) this.#scheduleStickyUpdate();
  }, 100);

  #updateStickyState = () => {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog?.open) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const contentHeight = content.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;

    // A measurement of zero means the layout is not settled yet. Deciding on it
    // would read as "no room at all" and latch the attribute to "false";
    // leaving the current value alone is always the safer answer.
    if (contentHeight <= 0 || summaryHeight <= 0) return;

    const room = contentHeight - summaryHeight;
    dialog.setAttribute('cart-summary-sticky', room < this.#minimumItemsRoom ? 'false' : 'true');
  };
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
